import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { toast } from "../../Components/Toast";
import { get, post } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

interface AuthState {
  user: any | null;
  isLoading: boolean;
  logoutLoading: boolean;
  forgotPasswordLoading: boolean;
  resetPasswordLoading: boolean;
  changePasswordLoading: boolean;
  publicKey: string | null;
  publicKeyLoading: boolean;
  publicKeyError: string | null;
  error: any | null;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: null,
  isLoading: false,
  logoutLoading: false,
  forgotPasswordLoading: false,
  resetPasswordLoading: false,
  changePasswordLoading: false,
  publicKey: null,
  publicKeyLoading: false,
  publicKeyError: null,
  error: null,
  isAuthenticated: false,
};

interface LoginCredentials {
  email: string;
  password: string;
}

interface ForgotPasswordData {
  email: string;
}

interface ResetPasswordData {
  token: string;
  new_password: string;
}

interface ChangePasswordData {
  old_password: string;
  new_password: string;
}

const unwrapContent = (response: any) => response?.content ?? response;

const getApiErrorMessage = (error: any, fallback: string) => {
  try {
    const parsed = JSON.parse(error?.message || "{}");
    return parsed?.message || fallback;
  } catch {
    return (
      error?.response?.data?.response?.message ||
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.message ||
      fallback
    );
  }
};

export const loginUser = createAsyncThunk(
  "auth/login",
  async (credentials: LoginCredentials) => {
    const encode = false;
    const response = await post(API_URLS.login(), credentials, encode);
    return unwrapContent(response);
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.logout(), {});
      return unwrapContent(response);
    } catch (error: any) {
      // Even if logout fails, we should still log the user out locally
      return rejectWithValue(getApiErrorMessage(error, "Failed to logout"));
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (data: ForgotPasswordData, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.forgotPassword(), data);
      return unwrapContent(response);
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(
          error,
          "Failed to send reset email. Please try again."
        )
      );
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (data: ResetPasswordData, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.resetPassword(), data);
      return unwrapContent(response);
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to reset password. Please try again.")
      );
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (data: ChangePasswordData, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.changePassword(), data);
      return unwrapContent(response);
    } catch (error: any) {
      return rejectWithValue(
        getApiErrorMessage(
          error,
          "Failed to change password. Please try again."
        )
      );
    }
  }
);

export const fetchPublicKey = createAsyncThunk(
  "auth/fetchPublicKey",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getKmsPublicKey());
      return response?.content?.public_key || response?.public_key || null;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to fetch public key. Please try again.";
      return rejectWithValue(errorMessage);
    }
  }
);

const AuthSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: state => {
      state.user = null;
      state.isAuthenticated = false;
    },
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        try {
          const errorMessage = action.error.message || "{}";
          const error = JSON.parse(errorMessage);
          state.error = error.message;
          toast.error(state.error || "Login failed");
        } catch (e) {
          state.error = "";
        }
      })
      .addCase(logoutUser.pending, state => {
        state.logoutLoading = true;
      })
      .addCase(logoutUser.fulfilled, state => {
        state.logoutLoading = false;
      })
      .addCase(logoutUser.rejected, state => {
        state.logoutLoading = false;
      })
      .addCase(forgotPassword.pending, state => {
        state.forgotPasswordLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, state => {
        state.forgotPasswordLoading = false;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPasswordLoading = false;
        state.error = action.payload as string;
      })
      .addCase(resetPassword.pending, state => {
        state.resetPasswordLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, state => {
        state.resetPasswordLoading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordLoading = false;
        state.error = action.payload as string;
      })
      .addCase(changePassword.pending, state => {
        state.changePasswordLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, state => {
        state.changePasswordLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.changePasswordLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPublicKey.pending, state => {
        state.publicKeyLoading = true;
        state.publicKeyError = null;
      })
      .addCase(fetchPublicKey.fulfilled, (state, action) => {
        state.publicKeyLoading = false;
        state.publicKey = action.payload;
        state.publicKeyError = null;
      })
      .addCase(fetchPublicKey.rejected, (state, action) => {
        state.publicKeyLoading = false;
        state.publicKeyError = action.payload as string;
      });
  },
});

export const { logout, clearError } = AuthSlice.actions;
export default AuthSlice.reducer;
